import { apiClient } from './client';
import type { EmailReplacementStatus } from '@/lib/types/email-replacement.types';

/**
 * Step 1 of the personal-to-company email replacement flow (FR-14, MYN-76):
 * submits the candidate company email via `POST /v1/me/email-replacement`.
 * The backend runs the free-mail blocklist and collision checks, creates the
 * `EmailReplacementRequest`, and sends an OTP to the current email.
 */
export async function startEmailReplacement(newEmail: string): Promise<EmailReplacementStatus> {
  const { data } = await apiClient.post<EmailReplacementStatus>('/me/email-replacement', { newEmail });
  return data;
}

/**
 * Step 2: verifies the OTP sent to the current email via
 * `POST /v1/me/email-replacement/:requestId/verify-current`. On success the
 * backend stamps `currentEmailVerifiedAt` and sends a second OTP to the new
 * email.
 */
export async function verifyCurrentEmailOtp(
  requestId: string,
  code: string,
): Promise<EmailReplacementStatus> {
  const { data } = await apiClient.post<EmailReplacementStatus>(
    `/me/email-replacement/${requestId}/verify-current`,
    { code },
  );
  return data;
}

/**
 * Step 3: verifies the OTP sent to the new email via
 * `POST /v1/me/email-replacement/:requestId/verify-new`. On success the
 * backend applies the swap (`User.email` updates in place) and the audit
 * write (FR-68) in one transaction, then checks for a pending invite match.
 */
export async function verifyNewEmailOtp(
  requestId: string,
  code: string,
): Promise<EmailReplacementStatus> {
  const { data } = await apiClient.post<EmailReplacementStatus>(
    `/me/email-replacement/${requestId}/verify-new`,
    { code },
  );
  return data;
}
